/**
 * Simulator Form Controller (EPIC-026.5)
 *
 * Manages the left-column form of the prospect simulator:
 *  - VL toggle (Oui/Non) → reveals parts_fiscales + rfr_n2 sub-block.
 *  - RFR inline validation against `rfrMaxPerShareEur × partsFiscales`.
 *  - Allocation chômage (none/ARE/ARCE) → reveals the relevant fields.
 *  - Multi-activités via `<template id="tpl-activity-secondary">` + cloneTemplate.
 *  - sessionStorage draft persistence under key `simulator.draft.v1`.
 *  - Debounced (400 ms) call to POST /simulator/calculate.
 *  - Inline 422 error display under each field via `[data-error-for]`.
 *  - Horizon switching (12 / 24 / 36 months).
 *  - Pre-fill from GET /aides/me (silent skip on 4xx).
 */

import BaseController from "./base_controller.js";
import { api } from "../services/api.js";
import { debounce } from "../utils/debounce.js";
import {
  serializeSimulatorForm,
  hydrateSimulatorForm,
} from "../utils/simulator_serializer.js";

const DRAFT_KEY = "simulator.draft.v1";

export default class SimulatorFormController extends BaseController {
  static targets = [
    "activitiesList",
    "activity",
    "vlConditional",
    "rfrAlert",
    "loadingState",
    "formColumn",
    "resultsColumn",
    "horizonButton",
  ];

  connect() {
    this.rates = null;
    this.rfrMaxPerShare = 28797;
    this.vatThreshold = 37500;

    this.debouncedCalculate = debounce(() => this.calculate(), 400);

    this.boundChangeHandler = () => this.onFormChange();
    this.element.addEventListener("input", this.boundChangeHandler);
    this.element.addEventListener("change", this.boundChangeHandler);

    this.restoreDraft();
    this.applyConditionalVisibility();
    this.tryPrefillFromProfile();
    this.loadRates().then(() => {
      this.validateRfr();
      this.calculate();
    });
  }

  disconnect() {
    if (this.debouncedCalculate) this.debouncedCalculate.cancel();
    this.element.removeEventListener("input", this.boundChangeHandler);
    this.element.removeEventListener("change", this.boundChangeHandler);
  }

  onFormChange() {
    this.applyConditionalVisibility();
    this.validateRfr();
    this.persistDraft();
    this.debouncedCalculate();
  }

  toggle(event) {
    const btn = event.currentTarget;
    const name = btn.dataset.toggle;
    if (!name) return;
    const value = btn.dataset.value;
    const buttons = this.element.querySelectorAll(`[data-toggle="${name}"]`);
    buttons.forEach((b) => {
      const matches = b === btn;
      b.setAttribute("aria-pressed", matches ? "true" : "false");
      b.classList.toggle("nm-btn--solid", matches);
      b.classList.toggle("nm-btn--ghost", !matches);
    });
    this.onFormChange();
  }

  setHorizon(event) {
    const btn = event.currentTarget;
    const months = String(btn.dataset.horizon || "12");
    this.horizonButtonTargets.forEach((b) => {
      const matches = b === btn;
      b.classList.toggle("nm-btn--solid", matches);
      b.classList.remove("nm-btn--ghost");
      if (!matches) b.classList.add("nm-btn--ghost");
    });
    const hidden = this.element.querySelector('[data-field="simulationMonths"]');
    if (hidden) hidden.value = months;
    this.onFormChange();
  }

  addActivity() {
    if (!this.hasActivitiesListTarget) return;
    const fragment = this.cloneTemplate("tpl-activity-secondary");
    const li = document.createElement("li");
    li.appendChild(fragment);
    this.activitiesListTarget.appendChild(li);
    this.onFormChange();
  }

  removeActivity(event) {
    const btn = event.currentTarget;
    const li = btn.closest("li");
    if (!li || !this.hasActivitiesListTarget) return;
    if (this.activitiesListTarget.querySelectorAll("li").length <= 1) return;
    li.remove();
    this.onFormChange();
  }

  reset() {
    sessionStorage.removeItem(DRAFT_KEY);
    window.location.reload();
  }

  // ---------------------------------------------------------------------------
  // Conditional UI
  // ---------------------------------------------------------------------------

  applyConditionalVisibility() {
    const state = serializeSimulatorForm(this.element);

    if (this.hasVlConditionalTarget) {
      this.vlConditionalTarget.hidden = !state.vlOption;
    }

    const tmiField = this.element.querySelector('[data-field-row="tmi"]');
    if (tmiField) tmiField.hidden = state.vlOption;

    const areRow = this.element.querySelector('[data-field-row="are"]');
    if (areRow) areRow.hidden = state.allocation !== "are";

    const arceRow = this.element.querySelector('[data-field-row="arce"]');
    if (arceRow) arceRow.hidden = state.allocation !== "arce";
  }

  validateRfr() {
    if (!this.hasRfrAlertTarget) return;
    const state = serializeSimulatorForm(this.element);
    if (!state.vlOption) {
      this.rfrAlertTarget.hidden = true;
      return;
    }
    const parts = Number(state.partsFiscales || 1);
    const rfr = Number(state.rfrN2 || 0);
    const max = this.rfrMaxPerShare * parts;
    this.rfrAlertTarget.hidden = !(rfr > max);
  }

  // ---------------------------------------------------------------------------
  // Persistence
  // ---------------------------------------------------------------------------

  persistDraft() {
    try {
      const state = serializeSimulatorForm(this.element);
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(state));
    } catch (_e) {
      // sessionStorage may be unavailable (private mode) — silent skip.
    }
  }

  restoreDraft() {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const state = JSON.parse(raw);
      hydrateSimulatorForm(this.element, state);
    } catch (_e) {
      // corrupted draft — ignore.
    }
  }

  // ---------------------------------------------------------------------------
  // Network
  // ---------------------------------------------------------------------------

  async tryPrefillFromProfile() {
    try {
      const res = await api.get("/aides/me");
      if (!res || !res.data) return;
      const conclusions = res.data.conclusions || res.data;
      this.markPrefilled(conclusions);
    } catch (_e) {
      // 4xx (compte gratuit sans tenant strict) → skip silencieux.
    }
  }

  markPrefilled(conclusions) {
    if (!conclusions || typeof conclusions !== "object") return;
    if (typeof conclusions.acreEligible === "boolean") {
      const btn = this.element.querySelector(
        `[data-toggle="acreEligible"][data-value="${conclusions.acreEligible ? "true" : "false"}"]`,
      );
      if (btn) btn.click();
      const acreFieldset = this.element.querySelector('[data-prefill="acre"]');
      if (acreFieldset) acreFieldset.dataset.prefilled = "true";
    }
    if (typeof conclusions.vlEligible === "boolean") {
      const btn = this.element.querySelector(
        `[data-toggle="vlOption"][data-value="${conclusions.vlEligible ? "true" : "false"}"]`,
      );
      if (btn) btn.click();
      const vlField = this.element.querySelector('[data-prefill="vl"]');
      if (vlField) vlField.dataset.prefilled = "true";
    }
    if (conclusions.allocationType) {
      const select = this.element.querySelector('[data-field="allocation"]');
      if (select) select.value = conclusions.allocationType;
      const alloc = this.element.querySelector('[data-prefill="allocation"]');
      if (alloc) alloc.dataset.prefilled = "true";
    }
  }

  async loadRates() {
    try {
      const res = await api.get("/simulator/rates");
      if (res && res.data) {
        this.rates = res.data;
        this.rfrMaxPerShare = res.data.rfrMaxPerShareEur || 28797;
        if (res.data.vatThresholds && res.data.vatThresholds.servicesEur) {
          this.vatThreshold = res.data.vatThresholds.servicesEur;
        }
        const cfeField = this.element.querySelector('[data-field="cfeAmount"]');
        if (cfeField && !cfeField.value) {
          cfeField.value = String(res.data.cfeMinimumEur || 243);
        }
      }
    } catch (_e) {
      // rates indisponibles → on garde les défauts. Pas bloquant.
    }
  }

  async calculate() {
    const state = serializeSimulatorForm(this.element);
    state.vatThreshold = this.vatThreshold;

    this.clearFieldErrors();
    this.setBusy(true);

    try {
      const res = await api.post("/simulator/calculate", state);
      this.renderResults(res, state);
    } catch (err) {
      if (err && err.status === 422 && err.data && err.data.error) {
        this.applyFieldErrors(err.data.error);
      } else if (err && err.status === 401) {
        // Centralized 401 handling already redirects via api.js
      } else {
        this.showError("Le calcul du simulateur a échoué. Réessayez.");
      }
    } finally {
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    if (this.hasResultsColumnTarget) {
      this.resultsColumnTarget.setAttribute("aria-busy", busy ? "true" : "false");
    }
    if (this.hasLoadingStateTarget) {
      this.loadingStateTarget.hidden = !busy;
    }
  }

  renderResults(payload, formState) {
    const resultsCtrl = this.application.getControllerForElementAndIdentifier(
      this.resultsColumnTarget,
      "simulator-results",
    );
    if (resultsCtrl && typeof resultsCtrl.render === "function") {
      resultsCtrl.render(payload, formState);
    }
  }

  applyFieldErrors(errorBlock) {
    const fields = (errorBlock.details && errorBlock.details.fields) || [];
    fields.forEach((f) => {
      const slot = this.element.querySelector(
        `[data-error-for="${cssEscape(f.field)}"]`,
      );
      if (slot) {
        slot.textContent = f.message || "Valeur invalide.";
        slot.hidden = false;
      }
    });
  }

  clearFieldErrors() {
    this.element.querySelectorAll("[data-error-for]").forEach((slot) => {
      slot.textContent = "";
      slot.hidden = true;
    });
  }
}

function cssEscape(value) {
  if (window.CSS && typeof window.CSS.escape === "function") {
    return window.CSS.escape(value);
  }
  return String(value).replace(/([!"#$%&'()*+,./:;<=>?@[\]^`{|}~])/g, "\\$1");
}
